import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ArtworkPageModel, ArtworkPageRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['artworkPages']

type ArtworkPageFilters = {
  artwork_id?: number,
}

type UseArtworkPagesOptions = {
  filters?: ArtworkPageFilters,
  performHttpRequests?: boolean,
  requestOptions?: ArtworkPageRequestOptions,
}

function useArtworkPages(options: UseArtworkPagesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    artwork_id: filterArtworkId,
  } = filters

  const {
    updatedEntities: { artworkPages: artworkPagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { artworkPages } = useSelector(reduxState => reduxState.entities)

  const { updating } = useSelector(reduxState => reduxState.artworkPages)

  const filteredArtworkPages = useMemo(() => {
    const filtered = Object.values(artworkPages).filter((artworkPage: ArtworkPageModel) => {
      const { artwork_id } = artworkPage

      const matchArtworkId = matchFilterNumber(artwork_id, filterArtworkId)

      return matchArtworkId
    })

    return sortArrayBy(filtered, 'asc', 'id')
  }, [artworkPagesUpdatedAt, JSON.stringify(filters)])

  const filteredArtworkPagesCount = filteredArtworkPages.length
  const hasFilteredArtworkPages = !!filteredArtworkPagesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredArtworkPagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingArtworkPages } = useReduxAction(
    'artworkPages',
    'loadArtworkPages',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredArtworkPages,
    filteredArtworkPagesCount,
    hasFilteredArtworkPages,
    loading: loadingArtworkPages,
    updating,
  }
}

export default useArtworkPages
